import type { PixelEvent } from '@/events';
import type { PixelModule } from '@/runtime/types';
import type { PixelRuntimeContext } from '@/runtime/PixelRuntimeContext';

import {
  IntegrationEngine,
  DestinationSubscriber,
  type IntegrationDebugState,
  type DestinationAdapter,
} from '@/integration';

/**
 * PixelModule facade — browser destinations only.
 * Subscribes Dispatcher in parallel with Transport.
 * Attribution is a separate Runtime service.
 */
export class IntegrationsModule implements PixelModule {
  readonly name = 'integrations' as const;

  private engine: IntegrationEngine | null = null;

  private subscriber: DestinationSubscriber | null = null;

  private active = false;

  init(context: PixelRuntimeContext): void {
    if (this.active) {
      context.logger.debug('[Pixel] Integrations module already initialized');
      return;
    }

    if (!context.config.modules.integrations.enabled) {
      context.logger.debug('[Pixel] Integrations module disabled');
      return;
    }

    const moduleConfig = context.config.modules.integrations;

    this.engine = new IntegrationEngine(context.logger, {
      moduleConfig: {
        ...moduleConfig,
        debug: moduleConfig.debug || context.config.app.debug === true,
      },
      getSessionId: () =>
        context.services.session.getSessionId() ??
        `anon_${context.config.identity.pixelId || context.config.identity.workspaceId || 'pixel'}`,
    });

    this.engine.init();

    this.subscriber = new DestinationSubscriber(
      context.services.dispatcher,
      event => this.engine?.handleEvent(event)
    );
    this.subscriber.subscribe();

    this.active = true;
    context.logger.debug('[Pixel] Integrations module initialized');
  }

  destroy(): void {
    if (!this.active) return;

    this.subscriber?.unsubscribeAll();
    this.subscriber = null;

    this.engine?.destroy();
    this.engine = null;

    this.active = false;
  }

  getDebugState(): IntegrationDebugState | null {
    return this.engine?.getDebugState() ?? null;
  }

  isEngineActive(): boolean {
    return this.engine?.isActive() ?? false;
  }

  registerDestination(adapter: DestinationAdapter): void {
    this.engine?.registerDestination(adapter);
  }

  removeDestination(name: string): boolean {
    return this.engine?.removeDestination(name) ?? false;
  }

  getMemoryRecords() {
    return this.engine?.getMemoryRecords() ?? [];
  }

  handleEventAsync(event: PixelEvent) {
    return this.engine?.handleEventAsync(event) ?? Promise.resolve([]);
  }
}
